import React, { useState, useEffect } from 'react'
import { Trash2, Edit2, MoreHorizontal, Send, CheckCircle, X, Download, FolderOpen, ChevronRight, Navigation2 } from 'lucide-react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import DeleteConfirmModal from '@/components/ui/DeleteConfirmModal'
import { designTokens } from '@/utils/design-tokens'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import { TaskTreeNode, Task, TaskItem, TaskGroup, TaskSubgroup, Business } from './TaskHierarchyTable'
import { TaskService } from '@/api/generated/services/TaskService'
import { TaskItemService } from '@/api/generated/services/TaskItemService'
import { TaskMapService } from '@/api/generated/services/TaskMapService'
import { TaskGroupService } from '@/api/generated/services/TaskGroupService'
import { TaskSubgroupService } from '@/api/generated/services/TaskSubgroupService'
import { BusinessService } from '@/api/generated/services/BusinessService'
import { FileService } from '@/api/generated/services/FileService'
import type { TaskItemCheckRequest } from '@/api/generated/models/TaskItemCheckRequest'
import type { FileResponse } from '@/api/generated/models/FileResponse'
import type { FileDownloadResponse } from '@/api/generated/models/FileDownloadResponse'
import type { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import type { TaskFilesResponse } from '@/api/generated/models/TaskFilesResponse'
import type { TaskBudgetItemFullResponse } from '@/api/generated/models/TaskBudgetItemFullResponse'
import type { TaskPerformanceItemFullResponse } from '@/api/generated/models/TaskPerformanceItemFullResponse'
import type { BusinessResponse } from '@/api/generated/models/BusinessResponse'
import type { TaskGroupResponse } from '@/api/generated/models/TaskGroupResponse'
import type { TaskSubGroupResponse } from '@/api/generated/models/TaskSubGroupResponse'
import type { TaskFullResponse } from '@/api/generated/models/TaskFullResponse'
import type { TaskItemResponse } from '@/api/generated/models/TaskItemResponse'
import { useAppSelector } from '@/store/hooks'
import { useCanUseAI } from '@/hooks/useCanUseAI'
import { useAlert } from '@/components/ui/Alert/exports'

// Utility function to format bytes to human-readable format
const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || bytes === 0) return '0 B'

  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, i)

  return `${size.toFixed(i === 0 ? 0 : 2)} ${sizes[i]}`
}

// 수행업무와 파일 정보
interface TaskItemWithFiles {
  id: string
  name: string
  status: string
  files: FileResponse[]
}

// 파일 요약 정보
interface TaskFilesSummary {
  taskItemCount: number
  totalFileCount: number
  isLoading: boolean
}

// ===== Type adapters: convert generated GET responses → TaskTreeNode =====
// 사용 위치: TaskDetailPanel 본문 fetch effect에서 type 별로 호출.

export type TaskNodeType = 'business' | 'task_group' | 'task_subgroup' | 'task' | 'task_item'

const createUser = (u: any) => ({
  id: u?.id || '',
  name: u?.name || '-',
  phone: u?.phone || '-',
  email: u?.email || '',
  account_id: u?.account_id || '',
})

function adaptBusiness(r: BusinessResponse): TaskTreeNode {
  const business: Business = {
    id: r.id,
    type: 'business',
    name: r.name || '',
    sort_key: String((r as any).sort_key ?? ''),
    workspace_id: r.workspace_id,
    workspace_name: r.workspace_name,
    task_groups: [],
  }
  return {
    id: r.id,
    type: 'business',
    name: r.name || '',
    orgHierarchy: [],
    originalData: business,
  }
}

function adaptTaskGroup(r: TaskGroupResponse): TaskTreeNode {
  const taskGroup: TaskGroup = {
    id: r.id,
    type: 'task_group',
    business_id: r.business_id,
    business_name: r.business_name,
    workspace_id: r.workspace_id,
    workspace_name: r.workspace_name,
    name: r.name,
    task_subgroups: [],
  }
  return {
    id: r.id,
    type: 'task_group',
    name: r.name,
    orgHierarchy: [r.business_name],
    originalData: taskGroup,
  }
}

function adaptTaskSubgroup(sub: TaskSubGroupResponse): TaskTreeNode {
  const taskSubgroup: TaskSubgroup = {
    id: sub.id,
    type: 'task_subgroup',
    task_group_id: sub.task_group_id,
    name: sub.name,
    tasks: [],
  }
  return {
    id: sub.id,
    type: 'task_subgroup',
    name: sub.name,
    orgHierarchy: [sub.business_name, sub.task_group_name].filter(Boolean) as string[],
    originalData: taskSubgroup,
  }
}

function adaptTask(r: TaskFullResponse): TaskTreeNode {
  const budget = r.budget && (r.budget.id || r.budget.result_description || (r.budget.items && r.budget.items.length > 0))
    ? {
        id: r.budget.id || '',
        result_description: r.budget.result_description || '',
        items: r.budget.items || [],
      }
    : null

  const performance = r.performance && (r.performance.id || r.performance.name || r.performance.unit || (r.performance.items && r.performance.items.length > 0))
    ? {
        id: r.performance.id || '',
        name: r.performance.name || '',
        unit: r.performance.unit || '',
        items: r.performance.items || [],
      }
    : null

  const task: Task = {
    id: r.id,
    type: 'task',
    task_subgroup_id: r.task_subgroup_id || '',
    name: r.name || '세부실행과제명 없음',
    origin_name: r.origin_name,
    department_id: r.department_id || '',
    department_name: r.department_name || '',
    assignee: createUser(r.assignee),
    sub_assignee: createUser(r.sub_assignee),
    manager: createUser(r.manager),
    reviewer: createUser(r.reviewer),
    budget: budget as any,
    performance: performance as any,
    task_items: r.task_items || [],
    status: r.status || '',
    start_at: r.start_at || '',
    end_at: r.end_at || '',
    labels: r.labels || [],
    detail_infos: r.detail_infos || [],
    extra_infos: r.extra_infos || [],
    task_group_name: r.task_group_name || '',
    task_subgroup_name: r.task_subgroup_name || '',
    business_name: r.business_name || '',
    business_id: r.business_id || '',
    is_origin: r.is_origin,
    is_linked: r.is_linked,
    is_performance_linked: r.is_performance_linked,
    task_map_id: r.task_map_id,
    task_map_overwrite_name: r.task_map_overwrite_name,
    // AI 분석 프롬프트에 함께 실리는 판단근거 (미설정이면 빈 문자열)
    judgment_basis: r.judgment_basis || '',
  } as Task

  return {
    id: r.id,
    type: 'task',
    name: r.name || '세부실행과제명 없음',
    origin_name: r.origin_name,
    status: r.status,
    business_name: r.business_name || '',
    department_name: r.department_name || '',
    assignee: createUser(r.assignee),
    sub_assignee: createUser(r.sub_assignee),
    manager: createUser(r.manager),
    reviewer: createUser(r.reviewer),
    orgHierarchy: [
      r.business_name || '',
      r.task_group_name || '',
      r.task_subgroup_name || '',
      r.name || '',
    ],
    originalData: task,
  }
}

function adaptTaskItem(r: TaskItemResponse): TaskTreeNode {
  const taskItem = {
    id: r.id,
    type: 'task_item',
    task_id: r.task_id,
    name: r.name,
    start_at: r.start_at,
    end_at: r.end_at,
    output: r.output || '',
    status: r.status,
    department_id: r.department_id,
    department_name: r.department_name,
    assignee: createUser(r.assignee),
    sub_assignee: createUser(r.sub_assignee),
    manager: createUser(r.manager),
    reviewer: createUser(r.reviewer),
    reviewer_approved: r.reviewer_approved,
    reviewer_approved_at: r.reviewer_approved_at,
    reviewer_comment: r.reviewer_comment,
    manager_approved: r.manager_approved,
    manager_approved_at: r.manager_approved_at,
    manager_comment: r.manager_comment,
    assignee_approved: r.assignee_approved,
    assignee_approved_at: r.assignee_approved_at,
    assignee_comment: r.assignee_comment,
  } as unknown as TaskItem

  return {
    id: r.id,
    type: 'task_item',
    name: r.name,
    status: r.status,
    business_name: r.business_name || '',
    department_name: r.department_name || '',
    assignee: createUser(r.assignee),
    sub_assignee: createUser(r.sub_assignee),
    manager: createUser(r.manager),
    reviewer: createUser(r.reviewer),
    orgHierarchy: [
      r.business_name || '',
      r.task_group_name || '',
      r.task_subgroup_name || '',
      r.task_name || '',
    ],
    originalData: taskItem,
  }
}

async function fetchTaskByType(id: string, type: TaskNodeType): Promise<TaskTreeNode> {
  switch (type) {
    case 'business':
      return adaptBusiness(await BusinessService.appsBusinessViewsGetBusiness({ businessId: id }))
    case 'task_group':
      return adaptTaskGroup(await TaskGroupService.appsTaskViewsGetTaskGroup({ groupId: id }))
    case 'task_subgroup':
      return adaptTaskSubgroup(await TaskSubgroupService.appsTaskViewsGetTaskSubgroup({ subGroupId: id }))
    case 'task':
      return adaptTask(await TaskService.appsTaskViewsGetTask({ taskId: id }))
    case 'task_item':
      return adaptTaskItem(await TaskItemService.appsTaskViewsGetTaskItem({ itemId: id }))
  }
}

interface TaskDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 표시할 항목의 ID. null이면 패널 내용 비움 */
  taskId: string | null
  /** 표시할 항목의 유형 (5종) */
  taskType: TaskNodeType | null
  /** 부모가 수정/삭제 성공 후 이 값을 증가시키면 패널이 자체적으로 재조회 */
  refreshKey?: number
  /** 수정 버튼 클릭 시: 패널이 내부 로드한 TaskTreeNode를 부모에 전달 */
  onEdit?: (task: TaskTreeNode) => void
  /** 삭제 후 부모에 알림용 (선택). 실제 삭제는 패널 내부에서 호출 */
  onDelete?: (task: TaskTreeNode, memo?: string) => void
  /** 액션 성공 후 부모 리스트 새로고침용 */
  onSuccess?: () => void
  /** 특정 과제로 스크롤 (TaskManagement 트리에서만 사용) */
  onScrollToTask?: (taskId: string) => void
  showDeleteButton?: boolean
}

// Section Header Component
const SectionHeader: React.FC<{
  title: string
  rightContent?: React.ReactNode
}> = ({ title, rightContent }) => (
  <div
    className="pb-2 border-b flex items-center justify-between"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
    {rightContent && (
      <div className="flex items-center gap-2">
        {rightContent}
      </div>
    )}
  </div>
)

// Info Field Component
const InfoField: React.FC<{ 
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean 
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6],
            whiteSpace: 'pre-wrap'
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

// Comment Component
const CommentSection: React.FC = () => {
  const [comment, setComment] = useState('')

  const handleAddComment = () => {
    if (comment.trim()) {
      console.warn('Adding comment:', comment)
      setComment('')
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          댓글 추가
        </label>
        <div className="flex gap-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="flex-1 p-3 border rounded resize-none"
            style={{
              borderColor: designTokens.colors.gray[1],
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              minHeight: '80px'
            }}
            placeholder="댓글을 입력하세요..."
          />
          <Button
            color="dark"
            size="small"
            onClick={handleAddComment}
            disabled={!comment.trim()}
            className="self-start"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// Approval Modal Component
interface ApprovalModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (comment?: string) => void
  title: string
  isSubmitting: boolean
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  isSubmitting
}) => {
  const [comment, setComment] = useState('')

  const handleSubmit = () => {
    onConfirm(comment.trim() || undefined)
  }

  const handleClose = () => {
    setComment('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[500px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
              disabled={isSubmitting}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Comment Input */}
            <div className="flex flex-col gap-2">
              <label
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                  fontWeight: 600,
                  color: designTokens.colors.gray[6]
                }}
              >
                코멘트 (선택사항)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="승인 관련 코멘트를 입력하세요..."
                rows={4}
                className="w-full p-3 border rounded resize-none"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2">
              <Button
                size="medium"
                color="white"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button
                size="medium"
                color="dark"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? '처리 중...' : '승인'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({
  isOpen,
  onClose,
  taskId,
  taskType,
  refreshKey,
  onEdit,
  onDelete,
  onSuccess,
  onScrollToTask,
  showDeleteButton = false,
}) => {
  // Get workspace from Redux state
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  // 판단근거는 AI 기능이 켜진 워크스페이스에서만 노출한다 (AI 분석 버튼과 동일 조건)
  const canUseAI = useCanUseAI()
  const { showError, showSuccess, showInfo } = useAlert()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // ===== 자체 데이터 로드 =====
  // 부모는 id/type만 넘기고, 패널이 알맞은 GET API를 호출해 표시 데이터를 만든다.
  const [loadedTask, setLoadedTask] = useState<TaskTreeNode | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !taskId || !taskType) {
      setLoadedTask(null)
      setLoadError(null)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    fetchTaskByType(taskId, taskType)
      .then(node => {
        if (!cancelled) setLoadedTask(node)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load task detail:', err)
        handleApiError(err, showError, ErrorMessages.FETCH_FAILED)
        setLoadError('데이터를 불러오는데 실패했습니다.')
        setLoadedTask(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, taskId, taskType, refreshKey, showError])

  // Local task state for immediate UI updates (optimistic updates after approval 등)
  const [currentTask, setCurrentTask] = useState<TaskTreeNode | null>(null)

  // Approval modal states
  const [isAssigneeApprovalModalOpen, setIsAssigneeApprovalModalOpen] = useState(false)
  const [isManagerApprovalModalOpen, setIsManagerApprovalModalOpen] = useState(false)
  const [isReviewerApprovalModalOpen, setIsReviewerApprovalModalOpen] = useState(false)

  // 패널이 닫힐 때 모든 모달 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setIsDeleteModalOpen(false)
      setIsDeleting(false)
      setDeleteError(null)
      setIsAssigneeApprovalModalOpen(false)
      setIsManagerApprovalModalOpen(false)
      setIsReviewerApprovalModalOpen(false)
      setIsApproving(false)
      setIsTaskItemFilesListOpen(false)
      setIsTaskFilesModalOpen(false)
    }
  }, [isOpen])
  const [isApproving, setIsApproving] = useState(false)

  // Attachment files state
  const [attachmentFiles, setAttachmentFiles] = useState<FileResponse[]>([])
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [filesError, setFilesError] = useState<string | null>(null)
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null)
  const [isBulkDownloading, setIsBulkDownloading] = useState(false)

  // Task files management state for 세부실행과제
  const [isTaskItemFilesListOpen, setIsTaskItemFilesListOpen] = useState(false)
  const [taskItemsWithFiles, setTaskItemsWithFiles] = useState<TaskItemWithFiles[]>([])
  const [isLoadingTaskFiles, setIsLoadingTaskFiles] = useState(false)
  const [taskFilesSummary, setTaskFilesSummary] = useState<TaskFilesSummary>({ taskItemCount: 0, totalFileCount: 0, isLoading: false })
  const [isTaskFilesModalOpen, setIsTaskFilesModalOpen] = useState(false)
  const [selectedTaskItemForFiles, setSelectedTaskItemForFiles] = useState<TaskItemWithFiles | null>(null)

  // 일괄 다운로드 진행 상태 관리
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState({
    totalFiles: 0,
    completedFiles: 0,
    failedFiles: 0,
    currentFileName: ''
  })

  // 연결세부실행과제명 수정 모드 상태
  const [isEditingLinkedTaskName, setIsEditingLinkedTaskName] = useState(false)
  const [linkedTaskNameInput, setLinkedTaskNameInput] = useState('')
  const [useOriginalName, setUseOriginalName] = useState(false)
  const [isUpdatingLinkedTaskName, setIsUpdatingLinkedTaskName] = useState(false)

  // Update local task state when props change
  useEffect(() => {
    setCurrentTask(loadedTask)
    // Reset task items with files when task changes to prevent stale data downloads
    setTaskItemsWithFiles([])
  }, [loadedTask])

  // Reset linked task name edit mode when task changes or panel closes
  useEffect(() => {
    setIsEditingLinkedTaskName(false)
    setLinkedTaskNameInput('')
    setUseOriginalName(false)
  }, [loadedTask, isOpen])

  // Fetch attachment files for task_item
  useEffect(() => {
    const fetchAttachmentFiles = async () => {
      if (!loadedTask || loadedTask.type !== 'task_item' || !isOpen) {
        setAttachmentFiles([])
        return
      }

      setIsLoadingFiles(true)
      setFilesError(null)

      try {
        const response = await FileService.appsFileViewsListFilesByTarget({
          targetId: loadedTask.id,
          targetType: 'task_item',
          page: 1,
          perPage: 100
        })

        if (response && Array.isArray(response.data)) {
          setAttachmentFiles(response.data as FileResponse[])
        } else {
          setAttachmentFiles([])
        }
      } catch (error) {
        console.error('Failed to fetch attachment files:', error)
        setFilesError('첨부파일을 불러오는데 실패했습니다.')
        setAttachmentFiles([])
      } finally {
        setIsLoadingFiles(false)
      }
    }

    fetchAttachmentFiles()
  }, [loadedTask, isOpen])

  // Fetch task files summary for 세부실행과제
  useEffect(() => {
    const fetchTaskFilesSummary = async () => {
      if (!loadedTask || loadedTask.type !== 'task' || !isOpen) {
        setTaskFilesSummary({ taskItemCount: 0, totalFileCount: 0, isLoading: false })
        return
      }

      setTaskFilesSummary(prev => ({ ...prev, isLoading: true }))

      try {
        const response: TaskFilesResponse = await FileService.appsFileViewsListFilesByTask({
          taskId: loadedTask.id,
          page: 1,
          perPage: 100
        })

        console.warn('Task files response:', response)

        if (response && response.task_items && Array.isArray(response.task_items)) {
          const taskItems = response.task_items
          console.warn('Task items from response:', taskItems)
          let totalFiles = 0

          taskItems.forEach(taskItem => {
            console.warn('Task item:', taskItem)
            if (taskItem.files && Array.isArray(taskItem.files)) {
              console.warn('Files in task item:', taskItem.files)
              totalFiles += taskItem.files.length
            }
          })

          console.warn('Final summary:', { taskItemCount: taskItems.length, totalFileCount: totalFiles })

          setTaskFilesSummary({
            taskItemCount: taskItems.length,
            totalFileCount: totalFiles,
            isLoading: false
          })
        } else {
          console.warn('Invalid response structure:', { response, hasTaskItems: !!response?.task_items, isArray: Array.isArray(response?.task_items) })
          setTaskFilesSummary({ taskItemCount: 0, totalFileCount: 0, isLoading: false })
        }
      } catch (error) {
        console.error('Failed to fetch task files summary:', error)
        setTaskFilesSummary({ taskItemCount: 0, totalFileCount: 0, isLoading: false })
      }
    }

    fetchTaskFilesSummary()
  }, [loadedTask, isOpen])

  // Refresh task data after approval/cancellation
  const refreshTaskData = async () => {
    if (!loadedTask || !selectedWorkspace?.id) return

    try {
      if (loadedTask.type === 'task_item') {
        // Fetch updated task item details
        const response = await TaskItemService.appsTaskViewsGetTaskItem({
          itemId: loadedTask.id,
        })

        // Update the task data in parent component and local state
        if (response) {
          const updatedTask = {
            ...loadedTask,
            status: response.status,
            assigneeApprovalStatus: response.assignee_approved,
            managerApprovalStatus: response.manager_approved,
            reviewerApprovalStatus: response.reviewer_approved,
            updatedAt: response.updated_at
          } as TaskTreeNode

          // Update local + loaded state immediately for UI (optimistic)
          setCurrentTask(updatedTask)
          setLoadedTask(updatedTask)
        }
      }
    } catch (error) {
      console.error('Failed to refresh task data:', error)
      // Don't show error to user as this is background refresh
    }
  }

  // Handle file download
  const handleFileDownload = async (file: FileResponse) => {
    setDownloadingFileId(file.id)

    try {
      const response: FileDownloadResponse = await FileService.appsFileViewsDownloadFile({
        fileId: file.id
      })

      // Try multiple approaches to force download
      try {
        // Method 1: Fetch and create blob URL (most reliable for forcing download)
        const fileResponse = await fetch(response.download_url)
        const blob = await fileResponse.blob()

        // Create blob URL and download
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = response.file_info.file_name

        // Force download attributes
        link.setAttribute('download', response.file_info.file_name)
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up blob URL
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100)

      } catch (fetchError) {
        // Method 2: Fallback to direct link with target="_blank" and download attribute
        console.warn('Blob download failed, using fallback method:', fetchError)

        const link = document.createElement('a')
        link.href = response.download_url
        link.download = response.file_info.file_name
        link.target = '_blank'
        link.rel = 'noopener noreferrer'

        // Force download attributes
        link.setAttribute('download', response.file_info.file_name)

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      showSuccess('파일 다운로드가 시작되었습니다.')
    } catch (error) {
      console.error('Failed to download file:', error)
      showError('파일 다운로드에 실패했습니다.')
    } finally {
      setDownloadingFileId(null)
    }
  }

  // Handle bulk download of all files
  const handleBulkDownload = async () => {
    if (attachmentFiles.length === 0) {
      showError('다운로드할 파일이 없습니다.')
      return
    }

    // 다운로드 가능한 파일 필터링 (file_exist가 true인 파일만)
    const validFiles = attachmentFiles.filter(file => file.file_exist !== false)
    if (validFiles.length === 0) {
      showError('다운로드 가능한 파일이 없습니다.')
      return
    }

    // 진행 상태 초기화
    setBulkDownloadProgress({
      totalFiles: validFiles.length,
      completedFiles: 0,
      failedFiles: 0,
      currentFileName: ''
    })

    setIsBulkDownloading(true)

    // 브라우저 다운로드 권한 안내 메시지
    showInfo('파일 다운로드가 되지 않는 경우, 브라우저 상단에서 다운로드 권한을 허용해주세요.')

    try {
      let successCount = 0
      let errorCount = 0

      // Download each file with a short delay to avoid overwhelming the server
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i]

        // 현재 다운로드 중인 파일명 업데이트
        setBulkDownloadProgress(prev => ({
          ...prev,
          currentFileName: file.origin_file_name
        }))

        try {
          const response: FileDownloadResponse = await FileService.appsFileViewsDownloadFile({
            fileId: file.id
          })

          // Use the same download logic as individual download
          try {
            // Method 1: Fetch and create blob URL
            const fileResponse = await fetch(response.download_url)
            const blob = await fileResponse.blob()

            const blobUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = response.file_info.file_name
            link.setAttribute('download', response.file_info.file_name)
            link.style.display = 'none'

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
            successCount++

            // 성공 시 완료 파일 수 증가
            setBulkDownloadProgress(prev => ({
              ...prev,
              completedFiles: prev.completedFiles + 1
            }))

          } catch (fetchError) {
            // Method 2: Fallback to direct link
            const link = document.createElement('a')
            link.href = response.download_url
            link.download = response.file_info.file_name
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            link.setAttribute('download', response.file_info.file_name)

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            successCount++

            // 성공 시 완료 파일 수 증가
            setBulkDownloadProgress(prev => ({
              ...prev,
              completedFiles: prev.completedFiles + 1
            }))
          }

          // Add delay between downloads to prevent browser blocking
          if (i < validFiles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }

        } catch (error) {
          console.error(`Failed to download file ${file.origin_file_name}:`, error)
          errorCount++

          // 실패 시 실패 파일 수 증가
          setBulkDownloadProgress(prev => ({
            ...prev,
            failedFiles: prev.failedFiles + 1
          }))
        }
      }

      // Show appropriate success/error message
      const skippedCount = attachmentFiles.length - validFiles.length
      if (successCount > 0 && errorCount === 0) {
        const message = skippedCount > 0
          ? `${successCount}개 파일 다운로드 완료 (${skippedCount}개 파일 건너뜀)`
          : `${successCount}개 파일의 다운로드가 시작되었습니다.`
        showSuccess(message)
      } else if (successCount > 0 && errorCount > 0) {
        const message = skippedCount > 0
          ? `${successCount}개 파일 다운로드 완료, ${errorCount}개 파일 다운로드 실패 (${skippedCount}개 파일 건너뜀)`
          : `${successCount}개 파일 다운로드 완료, ${errorCount}개 파일 다운로드 실패`
        showSuccess(message)
      } else {
        showError('모든 파일 다운로드에 실패했습니다.')
      }

    } catch (error) {
      console.error('Bulk download failed:', error)
      showError('파일 일괄 다운로드에 실패했습니다.')
    } finally {
      setIsBulkDownloading(false)

      // 진행률 상태 초기화
      setBulkDownloadProgress({
        totalFiles: 0,
        completedFiles: 0,
        failedFiles: 0,
        currentFileName: ''
      })
    }
  }

  // Fetch task items with files for the side panel
  const fetchTaskItemsWithFiles = async (): Promise<TaskItemWithFiles[]> => {
    if (!loadedTask || loadedTask.type !== 'task') return []

    setIsLoadingTaskFiles(true)

    try {
      const response: TaskFilesResponse = await FileService.appsFileViewsListFilesByTask({
        taskId: loadedTask.id,
        page: 1,
        perPage: 100
      })

      console.warn('Fetch task items with files response:', response)

      if (response && response.task_items && Array.isArray(response.task_items)) {
        const taskItemsWithFilesData: TaskItemWithFiles[] = response.task_items.map(item => {
          console.warn('Processing task item:', item)
          return {
            id: item.task_item_id,
            name: item.task_item_name,
            status: 'assigned', // TaskItemFilesResponse doesn't include status, using default
            files: item.files || []
          }
        })

        console.warn('Final task items with files data:', taskItemsWithFilesData)
        setTaskItemsWithFiles(taskItemsWithFilesData)
        return taskItemsWithFilesData
      } else {
        console.warn('Invalid response for task items with files:', { response, hasTaskItems: !!response?.task_items, isArray: Array.isArray(response?.task_items) })
        setTaskItemsWithFiles([])
        return []
      }
    } catch (error) {
      console.error('Failed to fetch task items with files:', error)
      setTaskItemsWithFiles([])
      showError('수행업무 파일 목록을 불러오는데 실패했습니다.')
      return []
    } finally {
      setIsLoadingTaskFiles(false)
    }
  }

  // Handle task item bulk download
  const handleTaskItemBulkDownload = async (taskItem: TaskItemWithFiles) => {
    if (!taskItem.files || taskItem.files.length === 0) {
      showError('다운로드할 파일이 없습니다.')
      return
    }

    // 다운로드 가능한 파일 필터링
    const validFiles = taskItem.files.filter(file => file.file_exist !== false)
    if (validFiles.length === 0) {
      showError('다운로드 가능한 파일이 없습니다.')
      return
    }

    setIsBulkDownloading(true)
    showInfo('파일 다운로드가 되지 않는 경우, 브라우저 상단에서 다운로드 권한을 허용해주세요.')

    try {
      let successCount = 0

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i]

        try {
          const response: FileDownloadResponse = await FileService.appsFileViewsDownloadFile({
            fileId: file.id
          })

          // Use the same download logic as individual download
          try {
            const fileResponse = await fetch(response.download_url)
            const blob = await fileResponse.blob()

            const blobUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = response.file_info.file_name
            link.setAttribute('download', response.file_info.file_name)
            link.style.display = 'none'

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
            successCount++

          } catch (fetchError) {
            const link = document.createElement('a')
            link.href = response.download_url
            link.download = response.file_info.file_name
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            link.setAttribute('download', response.file_info.file_name)

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            successCount++
          }

          // Add delay between downloads
          if (i < validFiles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }

        } catch (error) {
          console.error(`Failed to download file ${file.origin_file_name}:`, error)
        }
      }

      if (successCount > 0) {
        showSuccess(`${successCount}개 파일의 다운로드가 시작되었습니다.`)
      } else {
        showError('파일 다운로드에 실패했습니다.')
      }

    } catch (error) {
      console.error('Task item bulk download failed:', error)
      showError('파일 일괄 다운로드에 실패했습니다.')
    } finally {
      setIsBulkDownloading(false)
    }
  }

  // Handle opening task files list
  const handleOpenTaskFilesList = () => {
    fetchTaskItemsWithFiles()
    setIsTaskItemFilesListOpen(true)
  }

  // 연결세부실행과제명 수정 모드 시작
  const handleStartEditLinkedTaskName = () => {
    const taskOriginalData = currentTask?.originalData as Task
    // 현재 overwrite_name이 null이면 원본 명칭 사용 중
    const isUsingOriginal = !taskOriginalData?.task_map_overwrite_name

    setUseOriginalName(isUsingOriginal)
    setLinkedTaskNameInput(isUsingOriginal ? '' : (taskOriginalData?.task_map_overwrite_name || ''))
    setIsEditingLinkedTaskName(true)
  }

  // 연결세부실행과제명 수정 취소
  const handleCancelEditLinkedTaskName = () => {
    setIsEditingLinkedTaskName(false)
    setLinkedTaskNameInput('')
    setUseOriginalName(false)
  }

  // 연결세부실행과제명 저장
  const handleSaveLinkedTaskName = async () => {
    const taskOriginalData = currentTask?.originalData as Task
    if (!taskOriginalData?.task_map_id) return

    setIsUpdatingLinkedTaskName(true)
    const newOverwriteName = useOriginalName ? null : linkedTaskNameInput.trim()

    try {
      await TaskMapService.appsTaskViewsUpdateTaskMap({
        mapId: parseInt(taskOriginalData.task_map_id, 10),
        requestBody: {
          overwrite_name: newOverwriteName
        }
      })

      showSuccess('연결세부실행과제명이 수정되었습니다.')

      // 로컬 상태 즉시 업데이트
      if (currentTask) {
        const updatedOriginalData = {
          ...taskOriginalData,
          task_map_overwrite_name: newOverwriteName
        }
        // 원본 명칭 사용 시 origin_name 사용, 아니면 새 이름 사용
        const displayName = newOverwriteName || taskOriginalData.origin_name || taskOriginalData.name
        const updatedTask: TaskTreeNode = {
          ...currentTask,
          name: displayName,
          originalData: updatedOriginalData
        }
        setCurrentTask(updatedTask)
      }

      setIsEditingLinkedTaskName(false)
      setLinkedTaskNameInput('')
      setUseOriginalName(false)
      onSuccess?.()
    } catch (error) {
      console.error('Failed to update linked task name:', error)
      showError('연결세부실행과제명 수정에 실패했습니다.')
    } finally {
      setIsUpdatingLinkedTaskName(false)
    }
  }

  // 연결세부실행과제명 저장 버튼 활성화 조건
  const canSaveLinkedTaskName = useOriginalName || linkedTaskNameInput.trim().length > 0

  // Handle all task items bulk download with data parameter
  const handleAllTaskItemsBulkDownloadWithData = async (taskItemsData: TaskItemWithFiles[]) => {
    if (taskItemsData.length === 0) {
      showError('다운로드할 수행업무가 없습니다.')
      return
    }

    // 모든 수행업무의 파일들을 수집
    const allFiles: FileResponse[] = []
    taskItemsData.forEach(taskItem => {
      if (taskItem.files && taskItem.files.length > 0) {
        allFiles.push(...taskItem.files)
      }
    })

    if (allFiles.length === 0) {
      showError('다운로드할 파일이 없습니다.')
      return
    }

    // 다운로드 가능한 파일 필터링
    const validFiles = allFiles.filter(file => file.file_exist !== false)
    if (validFiles.length === 0) {
      showError('다운로드 가능한 파일이 없습니다.')
      return
    }

    setIsBulkDownloading(true)
    showInfo('파일 다운로드가 되지 않는 경우, 브라우저 상단에서 다운로드 권한을 허용해주세요.')

    try {
      let successCount = 0

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i]

        try {
          const response: FileDownloadResponse = await FileService.appsFileViewsDownloadFile({
            fileId: file.id
          })

          // Use the same download logic as individual download
          try {
            const fileResponse = await fetch(response.download_url)
            const blob = await fileResponse.blob()

            const blobUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = response.file_info.file_name
            link.setAttribute('download', response.file_info.file_name)
            link.style.display = 'none'

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
            successCount++

          } catch (fetchError) {
            const link = document.createElement('a')
            link.href = response.download_url
            link.download = response.file_info.file_name
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            link.setAttribute('download', response.file_info.file_name)

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            successCount++
          }

          // Add delay between downloads
          if (i < validFiles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }

        } catch (error) {
          console.error(`Failed to download file ${file.origin_file_name}:`, error)
        }
      }

      const skippedCount = allFiles.length - validFiles.length
      if (successCount > 0) {
        const message = skippedCount > 0
          ? `${successCount}개 파일 다운로드 완료 (${skippedCount}개 파일 건너뜀)`
          : `${successCount}개 파일의 다운로드가 시작되었습니다.`
        showSuccess(message)
      } else {
        showError('파일 다운로드에 실패했습니다.')
      }

    } catch (error) {
      console.error('All task items bulk download failed:', error)
      showError('전체 파일 일괄 다운로드에 실패했습니다.')
    } finally {
      setIsBulkDownloading(false)
    }
  }

  // Handle all task items bulk download
  const handleAllTaskItemsBulkDownload = async () => {
    await handleAllTaskItemsBulkDownloadWithData(taskItemsWithFiles)
  }

  // 패널이 닫혀있으면 아무것도 렌더하지 않음
  if (!isOpen) return null

  // 로딩 중 / 에러 / 빈 상태 → 패널 자체는 열린 채로 placeholder 표시
  if (!loadedTask || !currentTask) {
    return (
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="상세"
        width={800}
      >
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          {loadError ? (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.error.DEFAULT,
            }}>
              {loadError}
            </span>
          ) : isLoading ? (
            <div className="flex flex-col items-center gap-3">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: designTokens.colors.primary.DEFAULT }}
              />
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[5],
              }}>
                데이터를 불러오는 중...
              </span>
            </div>
          ) : (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[4],
            }}>
              표시할 항목이 없습니다.
            </span>
          )}
        </div>
      </SidePanel>
    )
  }

  const getTypeLabel = (type: string, originalData?: any) => {
    // Check if task is linked (is_origin === false)
    const isLinkedTask = type === 'task' &&
                        originalData &&
                        'is_origin' in originalData &&
                        originalData.is_origin === false

    const typeConfig: { [key: string]: string } = {
      business: '사업',
      task_group: '전략과제',
      task_subgroup: '실행과제',
      task: isLinkedTask ? '연결세부실행과제' : '세부실행과제',
      task_item: '수행업무'
    }
    return typeConfig[type] || type
  }

  const getStatusDisplay = (status: string | undefined) => {
    if (!status) return { label: '미설정', color: 'gray' }

    const statusConfig = {
      incomplete: { label: '미완료 (담당자)', color: 'error' },
      assigned: { label: '미완료 (관리자)', color: 'blue' },
      completed: { label: '완료 (관리자)', color: 'green' },
      reviewed: { label: '완료 (검토자)', color: 'blue' }
    }

    return statusConfig[status as keyof typeof statusConfig] || { label: status, color: 'gray' }
  }

  const getHierarchyDisplay = (task: TaskTreeNode) => {
    const { type, orgHierarchy } = task

    switch (type) {
      case 'business':
        return '-'
      case 'task_group':
        // 전략과제: 사업명
        return orgHierarchy[0] || '-'
      case 'task_subgroup':
        // 실행과제: 사업명 > 전략과제명
        return orgHierarchy.slice(0, 2).join(' > ') || '-'
      case 'task': {
        // 세부실행과제: business_name > task_group_name > task_subgroup_name 사용
        const taskOriginalData = task.originalData as Task
        const hierarchy = [
          taskOriginalData.business_name,
          taskOriginalData.task_group_name,
          taskOriginalData.task_subgroup_name
        ].filter(Boolean) // Remove any empty values
        return hierarchy.length > 0 ? hierarchy.join(' > ') : '-'
      }
      case 'task_item':
        // 수행업무: 사업명 > 전략과제명 > 실행과제명 > 세부실행과제명
        return orgHierarchy.slice(0, 4).join(' > ') || '-'
      default:
        return '-'
    }
  }

  const getTypeColor = (type: string) => {
    const colorConfig = {
      business: 'purple' as const,
      task_group: 'info' as const,
      task_subgroup: 'warning' as const,
      task: 'success' as const,
      task_item: 'gray' as const
    }
    return colorConfig[type as keyof typeof colorConfig] || 'gray' as const
  }

  // Check user role from localStorage
  // Get user role and id from localStorage
  const getUserInfo = (): { role: string; userId: string } => {
    try {
      const orgMemberStr = localStorage.getItem('dot_current_org_member')
      if (orgMemberStr) {
        const orgMember = JSON.parse(orgMemberStr)
        return {
          role: orgMember.role || 'normal',
          userId: orgMember.id || ''
        }
      }
    } catch (error) {
      console.error('Failed to parse org member data:', error)
    }
    return { role: 'normal', userId: '' }
  }

  /**
   * Check if user has permission to download files for a task_item
   * Permission granted if:
   * 1. User role is 'owner' or 'admin'
   * 2. User ID matches any task_item stakeholder (assignee, sub_assignee, manager, reviewer)
   */
  const canDownloadFiles = (task: TaskTreeNode | null): boolean => {
    if (!task) return false

    const { role, userId } = getUserInfo()

    // Condition 1: Admin/Owner always has access
    const isAdmin = role === 'owner' || role === 'admin'
    if (isAdmin) return true

    // Condition 2: User is a stakeholder of the task_item
    if (!userId) return false

    const assigneeId = task.assignee?.id || ''
    const subAssigneeId = task.sub_assignee?.id || ''
    const managerId = task.manager?.id || ''
    const reviewerId = task.reviewer?.id || ''

    const isStakeholder =
      userId === assigneeId ||
      userId === subAssigneeId ||
      userId === managerId ||
      userId === reviewerId

    return isStakeholder
  }

  /**
   * 수행업무 결재(확인/취소) 권한.
   *
   * - 담당자 확인/취소: 시스템 관리자(owner/admin), 수행업무 담당자, 수행업무 관리자
   * - 관리자 확인/취소: 시스템 관리자, 수행업무 관리자
   * - 검토자 확인/취소: 시스템 관리자, 수행업무 검토자
   *
   * 수행업무 관리자라도 검토자 결재는 대신할 수 없다.
   * 부담당자(sub_assignee)에게는 결재 권한이 없다.
   */
  const canApprove = (
    task: TaskTreeNode | null,
    approvalType: 'assignee' | 'manager' | 'reviewer'
  ): boolean => {
    if (!task || task.type !== 'task_item') return false

    const { role, userId } = getUserInfo()

    // Rule 1: Owner/Admin can do everything
    if (role === 'owner' || role === 'admin') return true

    // Rule 2: No userId means no stakeholder relationship
    if (!userId) return false

    // Get stakeholder IDs (부담당자는 결재 권한이 없어 비교하지 않는다)
    const assigneeId = task.assignee?.id || ''
    const managerId = task.manager?.id || ''
    const reviewerId = task.reviewer?.id || ''

    // Identify user's position
    const isAssignee = userId === assigneeId
    const isManager = userId === managerId
    const isReviewer = userId === reviewerId

    // Rule 3: 결재 유형별 권한
    switch (approvalType) {
      case 'assignee':
        // 담당자 확인/취소 — 담당자 본인 또는 수행업무 관리자
        return isAssignee || isManager
      case 'manager':
        // 관리자 확인/취소 — 수행업무 관리자만
        return isManager
      case 'reviewer':
        // 검토자 확인/취소 — 수행업무 검토자만 (관리자가 대신 할 수 없다)
        return isReviewer
      default:
        return false
    }
  }

  const headerActions = loadedTask.type === 'business' ? null : (loadedTask.type === 'task_group' || loadedTask.type === 'task_subgroup') ? (
    // 전략과제, 실행과제일 경우 owner, admin만 버튼 활성화
    (() => {
      const { role } = getUserInfo()
      const canEdit = role === 'owner' || role === 'admin'

      return (
        <>
          {showDeleteButton && (
            <Button
              size="small"
              color="dark"
              onClick={() => setIsDeleteModalOpen(true)}
              className="flex items-center gap-1"
              disabled={!canEdit}
            >
              <Trash2 className="w-4 h-4" />
              삭제
            </Button>
          )}
          <Button
            size="small"
            color="dark"
            onClick={() => onEdit?.(loadedTask)}
            className="flex items-center gap-1"
            disabled={!canEdit}
          >
            <Edit2 className="w-4 h-4" />
            수정
          </Button>
          {/*<Button*/}
          {/*  size="small"*/}
          {/*  color="dark"*/}
          {/*  onClick={() => console.warn('More', loadedTask.id)}*/}
          {/*  className="flex items-center gap-1"*/}
          {/*  disabled={!canEdit}*/}
          {/*>*/}
          {/*  <MoreHorizontal className="w-4 h-4" />*/}
          {/*  더보기*/}
          {/*</Button>*/}
        </>
      )
    })()
  ) : (loadedTask.type === 'task_item' || loadedTask.type === 'task') ? (
    // 세부실행과제, 수행업무일 경우 owner/admin 또는 담당자/부담당자만 버튼 활성화
    (() => {
      const { role, userId } = getUserInfo()
      
      // owner, admin은 무조건 편집 가능
      const isAdmin = role === 'owner' || role === 'admin'
      
      // 담당자 또는 부담당자인지 확인
      const assigneeId = loadedTask.assignee?.id || ''
      const subAssigneeId = loadedTask.sub_assignee?.id || ''
      const isAssignee = userId && (userId === assigneeId || userId === subAssigneeId)
      
      // 버튼 활성화 여부
      const canEdit = isAdmin || isAssignee

      // 삭제 버튼은 세부실행과제(task)의 경우 owner/admin만 가능
      const canDelete = loadedTask.type === 'task' ? isAdmin : canEdit

      return (
        <>
          {showDeleteButton && (
            <Button
              size="small"
              color="dark"
              onClick={() => setIsDeleteModalOpen(true)}
              className="flex items-center gap-1"
              disabled={!canDelete}
            >
              <Trash2 className="w-4 h-4" />
              삭제
            </Button>
          )}
          <Button
            size="small"
            color="dark"
            onClick={() => onEdit?.(loadedTask)}
            className="flex items-center gap-1"
            disabled={!canEdit}
          >
            <Edit2 className="w-4 h-4" />
            수정
          </Button>
          {/*<Button*/}
          {/*  size="small"*/}
          {/*  color="dark"*/}
          {/*  onClick={() => console.warn('More', loadedTask.id)}*/}
          {/*  className="flex items-center gap-1"*/}
          {/*  disabled={!canEdit}*/}
          {/*>*/}
          {/*  <MoreHorizontal className="w-4 h-4" />*/}
          {/*  더보기*/}
          {/*</Button>*/}
        </>
      )
    })()
  ) : (
    // 기타 타입일 경우 모든 버튼 표시
    <>
      {showDeleteButton && (
        <Button
          size="small"
          color="dark"
          onClick={() => setIsDeleteModalOpen(true)}
          className="flex items-center gap-1"
        >
          <Trash2 className="w-4 h-4" />
          삭제
        </Button>
      )}
      <Button
        size="small"
        color="dark"
        onClick={() => onEdit?.(loadedTask)}
        className="flex items-center gap-1"
      >
        <Edit2 className="w-4 h-4" />
        수정
      </Button>
      {/*<Button*/}
      {/*  size="small"*/}
      {/*  color="dark"*/}
      {/*  onClick={() => console.warn('More', loadedTask.id)}*/}
      {/*  className="flex items-center gap-1"*/}
      {/*>*/}
      {/*  <MoreHorizontal className="w-4 h-4" />*/}
      {/*  더보기*/}
      {/*</Button>*/}
    </>
  )

  // Get task data if available
  const taskData = loadedTask.type === 'task' ? (loadedTask.originalData as Task) : null

  const handleDeleteConfirm = async (memo?: string) => {
    if (!loadedTask) return
    
    setIsDeleting(true)
    setDeleteError(null)
    
    try {
      // For task (세부실행과제) type, use direct API call
      if (loadedTask.type === 'task') {
        const taskOriginalData = loadedTask.originalData as Task

        // Check if it's a linked task (is_origin === false)
        if (taskOriginalData.is_origin === false && taskOriginalData.task_map_id) {
          // Delete linked task using TaskMapService
          console.warn('Deleting linked task with TaskMapService.appsTaskViewsDeleteTaskMap:', taskOriginalData.task_map_id)
          await TaskMapService.appsTaskViewsDeleteTaskMap({
            mapId: parseInt(taskOriginalData.task_map_id, 10)
          })
          console.warn('Linked task deleted successfully:', taskOriginalData.task_map_id)
        } else {
          // Delete original task using TaskService
          console.warn('Deleting task with TaskService.appsTaskViewsDeleteTask:', loadedTask.id)
          await TaskService.appsTaskViewsDeleteTask({
            taskId: loadedTask.id,
            requestBody: { memo }
          })
          console.warn('Task deleted successfully:', loadedTask.id)
        }
      } else {
        // For other types, delegate to parent component
        await onDelete?.(loadedTask, memo)
      }
      
      setIsDeleteModalOpen(false)
      onClose()
      onSuccess?.()
    } catch (err) {
      console.error('Failed to delete:', err)
      
      // Provide specific error messages based on the error
      if (err && typeof err === 'object' && 'status' in err) {
        switch (err.status) {
          case 401:
            setDeleteError('인증이 만료되었습니다. 다시 로그인해주세요.')
            break
          case 404:
            setDeleteError('삭제하려는 항목을 찾을 수 없습니다.')
            break
          case 500:
            setDeleteError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            setDeleteError('삭제에 실패했습니다. 다시 시도해주세요.')
        }
      } else {
        setDeleteError('삭제에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  // Get approval button configuration based on status with semantic colors
  const getApprovalButtonConfig = (status: string | undefined, task?: TaskTreeNode | null) => {
    const config = {
      assignee: { text: '담당자 승인', approved: true, disabled: false, icon: CheckCircle, color: 'success' as const },
      manager: { text: '관리자 승인', approved: true, disabled: false, icon: CheckCircle, color: 'success' as const },
      reviewer: { text: '검토자 승인', approved: true, disabled: false, icon: CheckCircle, color: 'success' as const }
    }

    switch (status) {
      case 'reviewed':
        // All buttons should be "취소" (cancel)
        config.assignee = { text: '담당자 취소', approved: false, disabled: false, icon: X, color: 'danger' as const }
        config.manager = { text: '관리자 취소', approved: false, disabled: false, icon: X, color: 'danger' as const }
        config.reviewer = { text: '검토자 취소', approved: false, disabled: false, icon: X, color: 'danger' as const }
        break
      case 'completed':
        // Assignee and manager cancel, reviewer approve
        config.assignee = { text: '담당자 취소', approved: false, disabled: false, icon: X, color: 'danger' as const }
        config.manager = { text: '관리자 취소', approved: false, disabled: false, icon: X, color: 'danger' as const }
        config.reviewer = { text: '검토자 승인', approved: true, disabled: false, icon: CheckCircle, color: 'success' as const }
        break
      case 'assigned':
        // Assignee cancel, manager approve, reviewer approve (disabled)
        config.assignee = { text: '담당자 취소', approved: false, disabled: false, icon: X, color: 'danger' as const }
        config.manager = { text: '관리자 승인', approved: true, disabled: false, icon: CheckCircle, color: 'success' as const }
        config.reviewer = { text: '검토자 승인', approved: true, disabled: true, icon: CheckCircle, color: 'success' as const }
        break
      case 'incomplete':
      default:
        // Assignee approve, manager and reviewer approve (disabled)
        config.assignee = { text: '담당자 승인', approved: true, disabled: false, icon: CheckCircle, color: 'success' as const }
        config.manager = { text: '관리자 승인', approved: true, disabled: true, icon: CheckCircle, color: 'success' as const }
        config.reviewer = { text: '검토자 승인', approved: true, disabled: true, icon: CheckCircle, color: 'success' as const }
        break
    }

    // Apply permission-based disabled state (combines with workflow-based disabled)
    if (task) {
      config.assignee.disabled = config.assignee.disabled || !canApprove(task, 'assignee')
      config.manager.disabled = config.manager.disabled || !canApprove(task, 'manager')
      config.reviewer.disabled = config.reviewer.disabled || !canApprove(task, 'reviewer')
    }

    return config
  }

  // Handle approval confirmation
  const handleApprovalConfirm = async (type: 'assignee' | 'manager' | 'reviewer', approved: boolean, comment?: string) => {
    if (!loadedTask) return

    if (!selectedWorkspace?.id) {
      showError('워크스페이스 정보를 찾을 수 없습니다.')
      return
    }

    setIsApproving(true)

    try {
      const requestBody: TaskItemCheckRequest = {
        workspace_id: selectedWorkspace.id,
        type,
        approved,
        comment
      }

      await TaskItemService.appsTaskViewsCheckTaskItem({
        taskItemId: loadedTask.id,
        requestBody
      })

      // Close modals and refresh
      setIsAssigneeApprovalModalOpen(false)
      setIsManagerApprovalModalOpen(false)
      setIsReviewerApprovalModalOpen(false)

      // Show success message
      const typeLabels = {
        assignee: '담당자',
        manager: '관리자',
        reviewer: '검토자'
      }
      const action = approved ? '승인' : '취소'
      showSuccess(`${typeLabels[type]} ${action}이/가 완료되었습니다.`)

      // Refresh both side panel data and list data
      await refreshTaskData()
      onSuccess?.()

    } catch (err) {
      console.error(`Failed to ${type} approve:`, err)

      // Use common error handler with specific message per approval type
      const errorPrefixes = {
        assignee: ErrorMessages.ASSIGNEE_APPROVAL_FAILED,
        manager: ErrorMessages.MANAGER_APPROVAL_FAILED,
        reviewer: ErrorMessages.REVIEWER_APPROVAL_FAILED
      }
      handleApiError(err, showError, errorPrefixes[type])
    } finally {
      setIsApproving(false)
    }
  }

  const getDeleteMessage = () => {
    const typeLabel = getTypeLabel(loadedTask.type, loadedTask.originalData)
    return `${typeLabel}를 삭제하시겠습니까?\n하위 항목까지 모두 삭제됩니다.`
  }

  return (
    <>
      <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={`${getTypeLabel(currentTask.type, currentTask.originalData)} 상세`}
      width={800}
      titleActions={
        onScrollToTask && currentTask.id ? (
          <button
            onClick={() => onScrollToTask(currentTask.id)}
            className="flex items-center justify-center rounded transition-colors cursor-pointer hover:bg-gray-100"
            style={{
              width: '28px',
              height: '28px',
              padding: '0px',
              backgroundColor: 'transparent',
              border: 'none',
              color: designTokens.colors.gray[4]
            }}
            title="목록에서 위치 보기"
          >
            <Navigation2 className="w-4 h-4" />
          </button>
        ) : undefined
      }
      headerActions={headerActions}
    >
      <div className="relative">
        {loadError && (
          <div
            className="flex items-center justify-center"
            style={{
              minHeight: '200px',
              padding: '24px',
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.error.DEFAULT,
            }}
          >
            {loadError}
          </div>
        )}
        {/* Loading overlay */}
        {isLoading && (
          <div
            className="absolute inset-0 bg-white/80 flex items-center justify-center z-10"
            style={{
              minHeight: '400px'
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{
                  borderColor: designTokens.colors.primary.DEFAULT
                }}
              />
              <span
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  fontWeight: 400,
                  color: designTokens.colors.gray[5]
                }}
              >
                데이터를 조회 중입니다...
              </span>
            </div>
          </div>
        )}

        <div
          className="flex flex-col space-y-2"
          style={{
            padding: '24px',
          }}
        >
          {/* Section 1: 댓글 (only for task type) */}
        {/* TODO: 댓글 임시 비활성화 */}
        {/*{loadedTask.type === 'task' && (*/}
        {/*  <div className="space-y-2">*/}
        {/*    <SectionHeader title="댓글" />*/}
        {/*    <div style={{ padding: '0px 4px' }}>*/}
        {/*      <CommentSection />*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*)}*/}

        {/* Section 2: 기본정보 */}
        <div className="space-y-2">
          <SectionHeader
            title="기본정보"
            rightContent={
              currentTask.type === 'task_item' ? (() => {
                const buttonConfig = getApprovalButtonConfig(currentTask.status, loadedTask)
                const AssigneeIcon = buttonConfig.assignee.icon
                const ManagerIcon = buttonConfig.manager.icon
                const ReviewerIcon = buttonConfig.reviewer.icon

                return (
                  <>
                    <Button
                      size="small"
                      color={buttonConfig.assignee.color}
                      onClick={() => setIsAssigneeApprovalModalOpen(true)}
                      disabled={buttonConfig.assignee.disabled}
                      className="flex items-center gap-1"
                    >
                      <AssigneeIcon className="w-3 h-3" />
                      {buttonConfig.assignee.text}
                    </Button>
                    <Button
                      size="small"
                      color={buttonConfig.manager.color}
                      onClick={() => setIsManagerApprovalModalOpen(true)}
                      disabled={buttonConfig.manager.disabled}
                      className="flex items-center gap-1"
                    >
                      <ManagerIcon className="w-3 h-3" />
                      {buttonConfig.manager.text}
                    </Button>
                    <Button
                      size="small"
                      color={buttonConfig.reviewer.color}
                      onClick={() => setIsReviewerApprovalModalOpen(true)}
                      disabled={buttonConfig.reviewer.disabled}
                      className="flex items-center gap-1"
                    >
                      <ReviewerIcon className="w-3 h-3" />
                      {buttonConfig.reviewer.text}
                    </Button>
                  </>
                )
              })() : null
            }
          />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="유형" 
              value={
                <Tag color={getTypeColor(currentTask.type)} size="small">
                  ● {getTypeLabel(currentTask.type, currentTask.originalData)}
                </Tag>
              }
            />
            <InfoField 
              label="계층" 
              value={getHierarchyDisplay(loadedTask)}
            />
            {/* 연결세부실행과제인 경우 수정 가능한 UI 표시 */}
            {currentTask.type === 'task' && (currentTask.originalData as Task)?.is_origin === false ? (
              isEditingLinkedTaskName ? (
                // 수정 모드
                <div className="space-y-3">
                  <div className="flex flex-col gap-2">
                    <span
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        fontWeight: 400,
                        color: designTokens.colors.gray[6]
                      }}
                    >
                      연결세부실행과제명
                    </span>
                    <input
                      type="text"
                      value={useOriginalName ? (currentTask.origin_name || '') : linkedTaskNameInput}
                      onChange={(e) => setLinkedTaskNameInput(e.target.value)}
                      disabled={useOriginalName}
                      placeholder={useOriginalName ? '' : '명칭을 입력하세요'}
                      className="w-full px-3 py-2 border rounded outline-none"
                      style={{
                        borderColor: designTokens.colors.gray[1],
                        backgroundColor: useOriginalName ? designTokens.colors.gray[0] : 'white',
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        color: designTokens.colors.gray[6]
                      }}
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={useOriginalName}
                      onChange={(e) => setUseOriginalName(e.target.checked)}
                      className="w-4 h-4 cursor-pointer"
                      style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                    />
                    <span
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        color: designTokens.colors.gray[6]
                      }}
                    >
                      원본 명칭 사용
                    </span>
                  </label>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="small"
                      color="white"
                      onClick={handleCancelEditLinkedTaskName}
                      disabled={isUpdatingLinkedTaskName}
                    >
                      취소
                    </Button>
                    <Button
                      size="small"
                      color="dark"
                      onClick={handleSaveLinkedTaskName}
                      disabled={!canSaveLinkedTaskName || isUpdatingLinkedTaskName}
                    >
                      {isUpdatingLinkedTaskName ? '저장 중...' : '저장'}
                    </Button>
                  </div>
                </div>
              ) : (
                // 읽기 모드 - 연결세부실행과제
                <>
                  <InfoField
                    label="연결세부실행과제명"
                    value={
                      <div className="flex items-center justify-between w-full">
                        <span
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '16px',
                            fontWeight: 400,
                            color: designTokens.colors.gray[6]
                          }}
                        >
                          {currentTask.name}
                        </span>
                        <Button
                          size="small"
                          color="white"
                          onClick={handleStartEditLinkedTaskName}
                          className="flex items-center gap-1 ml-2"
                        >
                          <Edit2 className="w-3 h-3" />
                          수정
                        </Button>
                      </div>
                    }
                  />
                  {/* 원본 세부실행과제명 표시 */}
                  <InfoField
                    label="원본 세부실행과제명"
                    value={currentTask.origin_name || '-'}
                  />
                </>
              )
            ) : (
              // 원본 세부실행과제 또는 다른 유형
              <>
                <InfoField
                  label={`${getTypeLabel(currentTask.type, currentTask.originalData)}명`}
                  value={currentTask.name}
                />
                {/* 원본세부실행과제명 - task_map_overwrite_name이 있는 경우에만 표시 (원본 세부실행과제에서는 표시 안함) */}
              </>
            )}
            {/* 상태 정보 - 수행업무일 경우만 표시 */}
            {currentTask.type === 'task_item' && (
              <InfoField
                label="상태"
                value={
                  (() => {
                    const statusDisplay = getStatusDisplay(currentTask.status)
                    return (
                      <Tag color={statusDisplay.color} size="small">
                        ● {statusDisplay.label}
                      </Tag>
                    )
                  })()
                }
              />
            )}
            {/* 부서 및 담당자 정보 추가 (for task type) */}
            {loadedTask.type === 'task' && (
              <>
                <InfoField
                  label="부서"
                  value={loadedTask.department_name || '-'}
                />
                <InfoField
                  label="담당자"
                  value={loadedTask.assignee?.name || '-'}
                />
                <InfoField
                  label="부담당자"
                  value={loadedTask.sub_assignee?.name || '-'}
                />
                <InfoField
                  label="관리자"
                  value={loadedTask.manager?.name || '-'}
                />
                <InfoField
                  label="검토자"
                  value={loadedTask.reviewer?.name || '-'}
                />
                {canUseAI && (
                  <InfoField
                    label="판단근거"
                    value={(loadedTask.originalData as any)?.judgment_basis || '-'}
                  />
                )}
                <InfoField
                  label="수행업무 첨부파일"
                  value={
                    <div className="flex items-center justify-between w-full">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        fontWeight: 400,
                        color: designTokens.colors.gray[6]
                      }}>
                        {taskFilesSummary.isLoading
                          ? '로딩 중...'
                          : `수행업무 ${taskFilesSummary.taskItemCount}개, 파일 ${taskFilesSummary.totalFileCount}개`
                        }
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          size="small"
                          color="dark"
                          onClick={async () => {
                            try {
                              // 데이터가 없으면 먼저 로딩하고 받은 데이터로 직접 다운로드
                              let dataToUse = taskItemsWithFiles
                              if (taskItemsWithFiles.length === 0) {
                                dataToUse = await fetchTaskItemsWithFiles()
                              }

                              // 로딩 완료 후 받은 데이터로 다운로드 실행
                              await handleAllTaskItemsBulkDownloadWithData(dataToUse)
                            } catch (error) {
                              console.error('Bulk download failed:', error)
                            }
                          }}
                          disabled={!canDownloadFiles(loadedTask) || taskFilesSummary.isLoading || isBulkDownloading || taskFilesSummary.totalFileCount === 0}
                          className="flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          {isBulkDownloading ? '다운로드 중...' : '일괄 다운로드'}
                        </Button>
                        <Button
                          size="small"
                          color="white"
                          onClick={handleOpenTaskFilesList}
                          disabled={taskFilesSummary.isLoading}
                          className="flex items-center gap-1"
                        >
                          목록
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  }
                />
              </>
            )}
            {/* 수행업무 추가 정보 (for task_item type) */}
            {loadedTask.type === 'task_item' && (
              <>
                <InfoField 
                  label="부서" 
                  value={loadedTask.department_name || '-'}
                />
                <InfoField 
                  label="담당자" 
                  value={loadedTask.assignee?.name || '-'}
                />
                <InfoField 
                  label="부담당자" 
                  value={loadedTask.sub_assignee?.name || '-'}
                />
                <InfoField 
                  label="관리자" 
                  value={loadedTask.manager?.name || '-'}
                />
                <InfoField 
                  label="검토자" 
                  value={loadedTask.reviewer?.name || '-'}
                />
                <InfoField 
                  label="시작일" 
                  value={(loadedTask.originalData as TaskItem).start_at || '-'}
                />
                <InfoField 
                  label="종료일" 
                  value={(loadedTask.originalData as TaskItem).end_at || '-'}
                />
                <InfoField 
                  label="성과물" 
                  value={(loadedTask.originalData as TaskItem).output || '-'}
                />
              </>
            )}
          </div>
        </div>

        {/* Section 3: 라벨 (only for task type) */}
        {loadedTask.type === 'task' && (
          <>
            {/* 라벨 섹션 */}
            <div className="space-y-2">
              <SectionHeader title="라벨" />
              <div className="space-y-2" style={{ padding: '0px 4px' }}>
                {taskData?.labels && taskData.labels.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {taskData.labels.map((label) => (
                      <Tag key={label.id} color="gray" size="small">
                        {label.key}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: designTokens.colors.gray[4]
                  }}>
                    라벨 정보가 없습니다.
                  </div>
                )}
              </div>
            </div>
          </>
        )}


        {/* Section 5: 예산정보 (only for task type) */}
        {loadedTask.type === 'task' && taskData?.budget && (() => {
          // Helper function to calculate budget totals
          const calculateBudgetTotals = (items: TaskBudgetItemFullResponse[]) => {
            let totalTarget = 0
            let totalDone = 0

            items.forEach(item => {
              // Parse string to number, default to 0 if invalid
              const target = parseFloat(item.target_figure) || 0
              const done = parseFloat(item.done_figure) || 0

              totalTarget += target
              totalDone += done
            })

            return {
              totalTarget,
              totalDone
            }
          }

          // Helper function to format numbers with thousand separators
          const formatCurrency = (value: number): string => {
            return value.toLocaleString('ko-KR')
          }

          // Calculate aggregated budget totals
          const { totalTarget, totalDone } = calculateBudgetTotals(taskData.budget.items)

          return (
            <div className="space-y-2">
              <SectionHeader title="예산" />
              <div className="space-y-2" style={{ padding: '0px 4px' }}>
                <InfoField
                  label="예산 산출 내역"
                  value={taskData.budget.result_description || '-'}
                />
                <InfoField
                  label="예산 합계"
                  value={`목표: ${formatCurrency(totalTarget)} / 완료: ${formatCurrency(totalDone)}`}
                />
              </div>
            </div>
          )
        })()}

        {/* Section 6: 성과정보 (only for task type) */}
        {loadedTask.type === 'task' && taskData?.performance && (() => {
          // Helper function to calculate performance totals
          const calculatePerformanceTotals = (items: TaskPerformanceItemFullResponse[]) => {
            let totalTarget = 0
            let totalDone = 0

            items.forEach(item => {
              // Parse string to number, default to 0 if invalid/null/undefined
              const target = parseFloat(item.target_figure || '0') || 0
              const done = parseFloat(item.done_figure || '0') || 0

              totalTarget += target
              totalDone += done
            })

            return {
              totalTarget,
              totalDone
            }
          }

          // Helper function to format numbers with thousand separators
          const formatNumber = (value: number): string => {
            return value.toLocaleString('ko-KR')
          }

          // Calculate aggregated performance totals
          const { totalTarget, totalDone } = calculatePerformanceTotals(taskData.performance.items)

          return (
            <div className="space-y-2">
              <SectionHeader title="성과" />
              <div className="space-y-2" style={{ padding: '0px 4px' }}>
                <InfoField
                  label="성과명"
                  value={taskData.performance.name || '-'}
                />
                <InfoField
                  label="단위"
                  value={taskData.performance.unit || '-'}
                />
                <InfoField
                  label="성과 합계"
                  value={`목표: ${formatNumber(totalTarget)} / 완료: ${formatNumber(totalDone)}`}
                />
              </div>
            </div>
          )
        })()}

        {/* Section 7: 첨부파일 (only for task_item type) */}
        {loadedTask.type === 'task_item' && (
          <div className="space-y-2">
            <SectionHeader
              title="첨부파일"
              rightContent={
                attachmentFiles.length > 0 && (
                  <Button
                    size="small"
                    color="dark"
                    onClick={handleBulkDownload}
                    disabled={!canDownloadFiles(loadedTask) || isBulkDownloading || isLoadingFiles}
                    className="flex items-center gap-1"
                  >
                    <Download size={14} />
                    {isBulkDownloading
                      ? `다운로드 중... (${bulkDownloadProgress.completedFiles + bulkDownloadProgress.failedFiles}/${bulkDownloadProgress.totalFiles})`
                      : '일괄 다운로드'
                    }
                  </Button>
                )
              }
            />
            <div className="space-y-2" style={{ padding: '0px 4px' }}>
              {/* 일괄 다운로드 진행 상황 표시 */}
              {isBulkDownloading && (
                <div
                  className="p-4 border rounded"
                  style={{
                    borderColor: designTokens.colors.primary.light,
                    backgroundColor: '#F0F7FF'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Download size={16} className="text-blue-500 animate-pulse" />
                      <div>
                        <span style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '16px',
                          fontWeight: 600,
                          color: designTokens.colors.primary.DEFAULT
                        }}>
                          파일 일괄 다운로드 중...
                        </span>
                        <div style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '13px',
                          color: designTokens.colors.gray[4],
                          marginTop: '2px'
                        }}>
                          브라우저에서 다운로드 권한을 허용해주세요
                        </div>
                      </div>
                    </div>
                    <span style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 600,
                      color: designTokens.colors.primary.DEFAULT
                    }}>
                      {bulkDownloadProgress.completedFiles + bulkDownloadProgress.failedFiles}/{bulkDownloadProgress.totalFiles}
                    </span>
                  </div>

                  {/* 진행률 바 */}
                  <div className="w-full bg-blue-100 rounded-full h-2 mb-3">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${((bulkDownloadProgress.completedFiles + bulkDownloadProgress.failedFiles) / bulkDownloadProgress.totalFiles) * 100}%`,
                        backgroundColor: designTokens.colors.primary.DEFAULT
                      }}
                    />
                  </div>

                  {/* 상세 정보 */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        color: designTokens.colors.gray[5]
                      }}>
                        완료: {bulkDownloadProgress.completedFiles}개
                      </span>
                      {bulkDownloadProgress.failedFiles > 0 && (
                        <span style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          color: designTokens.colors.error.DEFAULT
                        }}>
                          실패: {bulkDownloadProgress.failedFiles}개
                        </span>
                      )}
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        color: designTokens.colors.gray[5]
                      }}>
                        남은 파일: {bulkDownloadProgress.totalFiles - bulkDownloadProgress.completedFiles - bulkDownloadProgress.failedFiles}개
                      </span>
                    </div>

                    {bulkDownloadProgress.currentFileName && (
                      <div style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '13px',
                        color: designTokens.colors.gray[4],
                        marginTop: '4px'
                      }}>
                        현재: {bulkDownloadProgress.currentFileName}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {isLoadingFiles ? (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: designTokens.colors.gray[4]
                }}>
                  첨부파일을 불러오는 중...
                </div>
              ) : filesError ? (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: designTokens.colors.error.DEFAULT
                }}>
                  {filesError}
                </div>
              ) : attachmentFiles.length > 0 ? (
                <div className="space-y-2">
                  {attachmentFiles.map((file, index) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 border rounded"
                      style={{
                        borderColor: designTokens.colors.gray[1],
                        backgroundColor: designTokens.colors.gray[0]
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: designTokens.colors.primary.DEFAULT }}
                        />
                        <div>
                          <div
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '16px',
                              fontWeight: 600,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            {file.origin_file_name}
                          </div>
                          <div
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '14px',
                              fontWeight: 400,
                              color: designTokens.colors.gray[4]
                            }}
                          >
                            {file.file_extension.toUpperCase()} 파일 • {formatFileSize(file.content_size)}
                            {file.created_at && ` • ${new Date(file.created_at).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace('T', ' ')}`}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="small"
                        color="dark"
                        onClick={() => handleFileDownload(file)}
                        disabled={!canDownloadFiles(loadedTask) || downloadingFileId === file.id || isBulkDownloading}
                      >
                        {downloadingFileId === file.id ? '다운로드 중...' : '다운로드'}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: designTokens.colors.gray[4]
                }}>
                  첨부파일이 없습니다.
                </div>
              )}
            </div>
          </div>
        )}

        {/* 상세정보와 추가정보 섹션을 가장 하단으로 이동 */}
        {loadedTask.type === 'task' && (
          <>
            {/* 상세정보 섹션 */}
            <div className="space-y-2">
              <SectionHeader title="상세정보" />
              <div className="space-y-2" style={{ padding: '0px 4px' }}>
                {taskData?.detail_infos && taskData.detail_infos.length > 0 ? (
                  <>
                    {taskData.detail_infos.map((info, index) => (
                      <InfoField
                        key={index}
                        label={info.name}
                        value={info.value || '-'}
                        fullWidth={false}
                      />
                    ))}
                  </>
                ) : (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: designTokens.colors.gray[4]
                  }}>
                    상세정보가 없습니다.
                  </div>
                )}
              </div>
            </div>

            {/* 추가정보 섹션 */}
            <div className="space-y-2">
              <SectionHeader title="추가정보" />
              <div className="space-y-2" style={{ padding: '0px 4px' }}>
                {taskData?.extra_infos && taskData.extra_infos.length > 0 ? (
                  <>
                    {taskData.extra_infos.map((info, index) => (
                      <InfoField
                        key={index}
                        label={info.name}
                        value={info.value || '-'}
                        fullWidth={false}
                      />
                    ))}
                  </>
                ) : (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: designTokens.colors.gray[4]
                  }}>
                    추가정보가 없습니다.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        </div>
      </div>
      </SidePanel>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeleteError(null)
        }}
        onConfirm={handleDeleteConfirm}
        title={`${getTypeLabel(loadedTask.type, loadedTask.originalData)} 삭제`}
        message={getDeleteMessage()}
        isDeleting={isDeleting}
        error={deleteError}
        showMemo
      />

      {/* Assignee Approval Modal */}
      {(() => {
        const buttonConfig = getApprovalButtonConfig(currentTask.status)
        return (
          <ApprovalModal
            isOpen={isAssigneeApprovalModalOpen}
            onClose={() => {
              setIsAssigneeApprovalModalOpen(false)
            }}
            onConfirm={(comment) => handleApprovalConfirm('assignee', buttonConfig.assignee.approved, comment)}
            title={buttonConfig.assignee.approved ? "담당자 승인" : "담당자 승인 취소"}
            isSubmitting={isApproving}
          />
        )
      })()}

      {/* Manager Approval Modal */}
      {(() => {
        const buttonConfig = getApprovalButtonConfig(currentTask.status)
        return (
          <ApprovalModal
            isOpen={isManagerApprovalModalOpen}
            onClose={() => {
              setIsManagerApprovalModalOpen(false)
            }}
            onConfirm={(comment) => handleApprovalConfirm('manager', buttonConfig.manager.approved, comment)}
            title={buttonConfig.manager.approved ? "관리자 승인" : "관리자 승인 취소"}
            isSubmitting={isApproving}
          />
        )
      })()}

      {/* Reviewer Approval Modal */}
      {(() => {
        const buttonConfig = getApprovalButtonConfig(currentTask.status)
        return (
          <ApprovalModal
            isOpen={isReviewerApprovalModalOpen}
            onClose={() => {
              setIsReviewerApprovalModalOpen(false)
            }}
            onConfirm={(comment) => handleApprovalConfirm('reviewer', buttonConfig.reviewer.approved, comment)}
            title={buttonConfig.reviewer.approved ? "검토자 승인" : "검토자 승인 취소"}
            isSubmitting={isApproving}
          />
        )
      })()}

      {/* Task Item Files List Side Panel */}
      <SidePanel
        isOpen={isTaskItemFilesListOpen}
        onClose={() => {
          setIsTaskItemFilesListOpen(false)
          setTaskItemsWithFiles([])
        }}
        title="수행업무 파일 목록"
        width={600}
        headerActions={
          taskItemsWithFiles.length > 0 && taskItemsWithFiles.some(item => item.files.length > 0) ? (
            <Button
              size="small"
              color="dark"
              onClick={handleAllTaskItemsBulkDownload}
              disabled={!canDownloadFiles(loadedTask) || isBulkDownloading}
              className="flex items-center gap-1"
            >
              <Download size={14} />
              {isBulkDownloading ? '다운로드 중...' : '전체 일괄 다운로드'}
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col space-y-4" style={{ padding: '24px' }}>
          {isLoadingTaskFiles ? (
            <div style={{
              padding: '32px',
              textAlign: 'center',
              color: designTokens.colors.gray[4]
            }}>
              수행업무 파일 목록을 불러오는 중...
            </div>
          ) : taskItemsWithFiles.length > 0 ? (
            <div className="space-y-3">
              {taskItemsWithFiles.map((taskItem) => (
                <div
                  key={taskItem.id}
                  className="p-4 border rounded cursor-pointer hover:bg-gray-50 transition-colors"
                  style={{ borderColor: designTokens.colors.gray[1] }}
                  onClick={() => {
                    setSelectedTaskItemForFiles(taskItem)
                    setIsTaskFilesModalOpen(true)
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <FolderOpen className="w-5 h-5" style={{ color: designTokens.colors.primary.DEFAULT }} />
                        <div>
                          <div style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '16px',
                            fontWeight: 600,
                            color: designTokens.colors.gray[6]
                          }}>
                            {taskItem.name}
                          </div>
                          <div style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '14px',
                            color: designTokens.colors.gray[4],
                            marginTop: '2px'
                          }}>
                            파일 {taskItem.files.length}개 • 상태: {getStatusDisplay(taskItem.status).label}
                          </div>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4" style={{ color: designTokens.colors.gray[4] }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              padding: '32px',
              textAlign: 'center',
              color: designTokens.colors.gray[4]
            }}>
              파일이 첨부된 수행업무가 없습니다.
            </div>
          )}
        </div>
      </SidePanel>

      {/* Task Files Modal */}
      {isTaskFilesModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          data-modal-overlay
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Background Overlay */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
            onClick={() => {
              setIsTaskFilesModalOpen(false)
              setSelectedTaskItemForFiles(null)
            }}
          />

          {/* Modal Content */}
          <div
            className="relative bg-white rounded w-[800px] max-w-[90vw] max-h-[90vh] overflow-auto border"
            style={{
              borderColor: designTokens.colors.gray[1]
            }}
          >
            <div className="flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between gap-[10px] p-[30px] pb-[20px] border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
                <h2
                  className="font-bold"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['title-medium'][0],
                    lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                    fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                    color: designTokens.colors.gray[6]
                  }}
                >
                  {selectedTaskItemForFiles ? `${selectedTaskItemForFiles.name} - 첨부파일` : '첨부파일'}
                </h2>
                <div className="flex items-center gap-2">
                  {selectedTaskItemForFiles && selectedTaskItemForFiles.files.length > 0 && (
                    <Button
                      size="small"
                      color="dark"
                      onClick={() => handleTaskItemBulkDownload(selectedTaskItemForFiles)}
                      disabled={!canDownloadFiles(loadedTask) || isBulkDownloading}
                      className="flex items-center gap-1"
                    >
                      <Download size={14} />
                      {isBulkDownloading ? '다운로드 중...' : '일괄 다운로드'}
                    </Button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setIsTaskFilesModalOpen(false)
                      setSelectedTaskItemForFiles(null)
                    }}
                    className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
                    style={{ color: designTokens.colors.gray[5] }}
                  >
                    <X className="w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4" style={{ padding: '24px' }}>
                {selectedTaskItemForFiles && selectedTaskItemForFiles.files.length > 0 ? (
                  <div className="space-y-3">
                    {selectedTaskItemForFiles.files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 border rounded"
                        style={{
                          borderColor: designTokens.colors.gray[1],
                          backgroundColor: designTokens.colors.gray[0]
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: designTokens.colors.primary.DEFAULT }}
                          />
                          <div>
                            <div style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '16px',
                              fontWeight: 600,
                              color: designTokens.colors.gray[6]
                            }}>
                              {file.origin_file_name}
                            </div>
                            <div style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '14px',
                              fontWeight: 400,
                              color: designTokens.colors.gray[4]
                            }}>
                              {file.file_extension.toUpperCase()} 파일 • {formatFileSize(file.content_size)}
                              {file.file_exist === false && (
                                <span style={{
                                  color: designTokens.colors.error.DEFAULT,
                                  marginLeft: '8px'
                                }}>
                                  • 파일이 유효하지 않습니다
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="small"
                          color="dark"
                          onClick={() => handleFileDownload(file)}
                          disabled={!canDownloadFiles(loadedTask) || downloadingFileId === file.id || file.file_exist === false || isBulkDownloading}
                        >
                          {downloadingFileId === file.id ? '다운로드 중...' : '다운로드'}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: designTokens.colors.gray[4]
                  }}>
                    첨부파일이 없습니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default TaskDetailPanel